<template>
  <nav
    :class="{ sidebar: true, sidebarStatic, sidebarOpened }"
    @mouseenter="sidebarMouseEnter"
    @mouseleave="sidebarMouseLeave"
  >
    <header class="logo">
      <router-link :to="{ name: 'home' }">
        <img
          class="mb-1 mr-3 mt-3 sidebar-logo"
          src="../../assets/retis_small.png"
          alt="Retis Energy"
        />
      </router-link>
    </header>

    <div class="nav-container">
      <RoleNavigation v-if="showsRoleNavigation" :items="navigationItems" />
      <AdminNavigation v-else :activeItem="activeItem" />
    </div>

    <div class="sidebar-footer">
      <ul class="sidebarLabels">
        <li class="sidebar-username">
          {{ getCurrentUser.firstname }} {{ getCurrentUser.lastname }}
        </li>
        <li v-if="isAdmin" class="sidebar-navigation-preview">
          <label class="sidebar-navigation-preview__toggle">
            <input
              type="checkbox"
              data-test="navigation-preview-toggle"
              :checked="isNavigationPreviewOn"
              @change="changeNavigationPreview($event.target.checked)"
            />
            {{ $t('navigationPreview_toggle') }}
          </label>
          <small v-if="isNavigationPreviewOn && hasMyAccessError" class="d-block">
            {{ $t('navigationPreview_unavailable') }}
          </small>
        </li>
        <li>
          <div>
            <small>version {{ version }}</small>
          </div>
          <div>
            <small>&copy; {{ $dayjs().format('YYYY') }} retis.energy</small>
          </div>
        </li>
      </ul>
    </div>
  </nav>
</template>

<script>
import { mapState, mapActions, mapGetters as mapRootGetters, createNamespacedHelpers } from 'vuex';
import isScreen from '@/core/screenHelper';
import { buildNavigationItems } from '@/navigation/buildNavigationItems';
import { NAVIGATION_ROUTES } from '@/navigation/navigationRoutes';
import {
  isNavigationPreviewEnabled,
  setNavigationPreviewEnabled,
} from '@/navigation/navigationPreview';
import AdminNavigation from './AdminNavigation';
import ClientNavigation from './ClientNavigation';
import RoleNavigation from './RoleNavigation';

const { mapGetters } = createNamespacedHelpers('auth');

export default {
  name: 'Sidebar',
  components: {
    AdminNavigation,
    RoleNavigation,
    // ClientNavigation,
  },
  data() {
    return {
      version: process.env.VUE_APP_VERSION,
      showDemoContent: false,
      isNavigationPreviewOn: isNavigationPreviewEnabled(),
    };
  },
  methods: {
    ...mapActions('layout', ['changeSidebarActive', 'switchSidebar']),
    ...mapActions(['fetchMyAccess']),
    /** Loads `myAccess` once when the new navigation is wanted; errors only set the store flag. */
    loadMyAccessWhenWanted() {
      if (this.wantsRoleNavigation && !this.isMyAccessLoaded) {
        this.fetchMyAccess();
      }
    },
    /** Loading follows through the `wantsRoleNavigation` watcher. */
    changeNavigationPreview(isEnabled) {
      setNavigationPreviewEnabled(isEnabled);
      this.isNavigationPreviewOn = isEnabled;
    },
    setActiveByRoute() {
      const paths = this.$route.fullPath.split('/');
      paths.pop();
      this.changeSidebarActive(paths.join('/'));
    },
    sidebarMouseEnter() {
      if (!this.sidebarStatic && (isScreen('lg') || isScreen('xl'))) {
        this.switchSidebar(false);
        this.setActiveByRoute();
      }
    },
    sidebarMouseLeave() {
      if (!this.sidebarStatic && (isScreen('lg') || isScreen('xl'))) {
        this.switchSidebar(true);
        this.changeSidebarActive(null);
      }
    },
  },
  watch: {
    /** e.g. the organization was switched on while the app is open */
    wantsRoleNavigation() {
      this.loadMyAccessWhenWanted();
    },
  },
  created() {
    this.setActiveByRoute();
    this.loadMyAccessWhenWanted();
  },
  computed: {
    ...mapGetters(['getCurrentUser', 'isAdmin', 'isClient', 'isOrganizationRoleNavigationEnabled']),
    ...mapRootGetters(['getMyAccessNodes', 'isMyAccessLoaded', 'hasMyAccessError']),
    /** Switched on for the whole organization, or previewed by an admin. */
    wantsRoleNavigation() {
      return (
        this.isOrganizationRoleNavigationEnabled || (this.isAdmin && this.isNavigationPreviewOn)
      );
    },
    /** Only with an answer from `myAccess`; on error everyone keeps the old navigation. */
    showsRoleNavigation() {
      return this.wantsRoleNavigation && this.isMyAccessLoaded && !this.hasMyAccessError;
    },
    navigationItems() {
      return buildNavigationItems(this.getMyAccessNodes, NAVIGATION_ROUTES, (labelKey) =>
        this.$t(labelKey)
      );
    },
    ...mapState('layout', {
      sidebarStatic: (state) => state.sidebarStatic,
      sidebarOpened: (state) => !state.sidebarClose,
      activeItem: (state) => state.sidebarActiveElement,
    }),
  },
};
</script>

<!-- Sidebar styles should be scoped -->
<style src="./Sidebar.scss" lang="scss" scoped />
